import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";

import { REQUEST_STATUSES } from "@/constants/requestStatuses";
import { PropertyService } from "@/services/PropertyService";
import type { State } from "@/store/types";

import type { OrgUser } from "../currentUser/selectors";

export interface PropertyOrganizationUsersState {
  [propertyId: number]: typeof defaultState;
}

const defaultState = {
  data: [] as OrgUser[],
  status: REQUEST_STATUSES.IDLE as REQUEST_STATUSES,
  count: 0 as number,
  err: null as unknown,
  noResults: false as boolean,
} as const;

const initialState: PropertyOrganizationUsersState = {};

export const fetchPropertyOrganizationUsers = createAsyncThunk(
  "propertyOrganizationUsers/fetchPropertyOrganizationUsers",
  async ({ propertyId, query }: { propertyId: number; query?: unknown }) => {
    const { resp, status, err } = await PropertyService.getOrganizationUsersByPropertyId(propertyId, query);
    const { data = [], count = 0 } = resp || {};

    return {
      propertyId,
      data,
      count,
      status,
      err,
    };
  },
);

const propertyOrganizationUsersSlice = createSlice({
  name: "propertyOrganizationUsers",
  initialState,
  reducers: {
    resetPropertyOrganizationUsers: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPropertyOrganizationUsers.pending, (state, action) => {
        const propertyId = action.meta.arg.propertyId;
        if (!state[propertyId]) {
          state[propertyId] = { ...defaultState };
        }
        state[propertyId].status = REQUEST_STATUSES.LOADING;
        state[propertyId].err = null;
      })
      .addCase(fetchPropertyOrganizationUsers.fulfilled, (state, action) => {
        const { propertyId, data, status, err, count } = action.payload;
        state[propertyId] = {
          ...state[propertyId],
          data,
          status,
          count,
          err,
          noResults: data.length === 0,
        };
      })
      .addCase(fetchPropertyOrganizationUsers.rejected, (state, action) => {
        const propertyId = action.meta.arg.propertyId;
        if (state[propertyId]) {
          state[propertyId].status = REQUEST_STATUSES.FAILED;
          state[propertyId].err = action.error;
        }
      });
  },
});

export const propertyOrganizationUsersSelector = (propertyId: number) =>
  createSelector(
    (state: State) => state.propertyOrganizationUsers,
    (propertyOrganizationUsers) => ({
      ...defaultState,
      ...propertyOrganizationUsers?.[propertyId],
    }),
  );

export default propertyOrganizationUsersSlice.reducer;
